// SCR-03·03D API 훅 — 플랜 §5 엔드포인트 기준
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import { unwrapList } from '../../lib/api/queries'
import type {
  ActivityHistory,
  Asset,
  Client,
  ClientPayload,
  Document,
  Paginated,
  ProjectClientMap,
  ReportDelivery,
  ReportRecipient,
} from '../../types'

export interface ClientFilters {
  client_type?: string
  contract_status?: string
  manager_id?: string
  search?: string
  page: number
  page_size: number
}

export function useClients(filters: ClientFilters) {
  return useQuery({
    queryKey: ['clients', filters],
    queryFn: async () => {
      const params: Record<string, string | number> = {
        page: filters.page,
        page_size: filters.page_size,
      }
      if (filters.client_type) params.client_type = filters.client_type
      if (filters.contract_status) params.contract_status = filters.contract_status
      if (filters.manager_id) params.manager_id = filters.manager_id
      if (filters.search) params.search = filters.search
      const { data } = await api.get<Client[] | Paginated<Client>>('/clients', { params })
      return unwrapList(data)
    },
  })
}

export function useClient(clientId: string | undefined) {
  return useQuery({
    queryKey: ['clients', clientId],
    queryFn: async () => {
      const { data } = await api.get<Client>(`/clients/${clientId}`)
      return data
    },
    enabled: !!clientId,
  })
}

export function useSaveClient(clientId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ClientPayload) => {
      const { data } = clientId
        ? await api.put<Client>(`/clients/${clientId}`, payload)
        : await api.post<Client>('/clients', payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
  })
}

/** 고객사 삭제 — 종속 없으면 바로 삭제(있으면 409). force+confirmName(담당자 본인 이름)으로
 *  종속까지 강제 삭제(단 사업/정산 있으면 백엔드가 강제여도 409). */
export function useDeleteClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (args: { clientId: string; force?: boolean; confirmName?: string }) => {
      const p = new URLSearchParams()
      if (args.force) {
        p.set('force', 'true')
        if (args.confirmName) p.set('confirm_name', args.confirmName)
      }
      const qs = p.toString()
      await api.delete(`/clients/${args.clientId}${qs ? `?${qs}` : ''}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
  })
}

// ── 상세 탭 데이터 ──────────────────────────────────────────────────
export function useClientHistories(clientId: string | undefined) {
  return useQuery({
    queryKey: ['clients', clientId, 'histories'],
    queryFn: async () => {
      const { data } = await api.get<ActivityHistory[] | Paginated<ActivityHistory>>(
        `/clients/${clientId}/histories`,
      )
      return unwrapList(data).items
    },
    enabled: !!clientId,
  })
}

export function useClientReports(clientId: string | undefined) {
  return useQuery({
    queryKey: ['clients', clientId, 'reports'],
    queryFn: async () => {
      const { data } = await api.get<ReportDelivery[] | Paginated<ReportDelivery>>(
        `/clients/${clientId}/reports`,
      )
      return unwrapList(data).items
    },
    enabled: !!clientId,
  })
}

export function useClientDocuments(clientId: string | undefined) {
  return useQuery({
    queryKey: ['clients', clientId, 'documents'],
    queryFn: async () => {
      const { data } = await api.get<Document[] | Paginated<Document>>(
        `/clients/${clientId}/documents`,
      )
      return unwrapList(data).items
    },
    enabled: !!clientId,
  })
}

export function useClientProjects(clientId: string | undefined) {
  return useQuery({
    queryKey: ['clients', clientId, 'projects'],
    queryFn: async () => {
      const { data } = await api.get<ProjectClientMap[] | Paginated<ProjectClientMap>>(
        `/clients/${clientId}/projects`,
      )
      return unwrapList(data).items
    },
    enabled: !!clientId,
  })
}

// ── 보고서 수신자 (tb_report_recipient, R2-B8) ──────────────────────
export function useClientRecipients(clientId: string | undefined) {
  return useQuery({
    queryKey: ['clients', clientId, 'recipients'],
    queryFn: async () => {
      const { data } = await api.get<ReportRecipient[]>(`/clients/${clientId}/recipients`)
      return data
    },
    enabled: !!clientId,
  })
}

export interface RecipientPayload {
  email: string
  name?: string
  cc_yn?: string // Y=CC / N=TO
  sub_id?: string // 미지정=전 유형 공통
}

export function useAddRecipient(clientId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: RecipientPayload) => {
      const { data } = await api.post<ReportRecipient>(
        `/clients/${clientId}/recipients`,
        payload,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'recipients'] })
    },
  })
}

export function useRemoveRecipient(clientId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (recipientId: string) => {
      const { data } = await api.delete(`/clients/${clientId}/recipients/${recipientId}`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'recipients'] })
    },
  })
}

export function useClientAssets(clientId: string | undefined) {
  return useQuery({
    queryKey: ['clients', clientId, 'assets'],
    queryFn: async () => {
      const { data } = await api.get<Asset[] | Paginated<Asset>>(`/clients/${clientId}/assets`)
      return unwrapList(data).items
    },
    enabled: !!clientId,
  })
}
